/**
 * PRD.262 — Patient Package Redemption.
 *
 * Imperative operations against the local PowerSync DB that redeem package
 * benefits and reverse redemptions. Pure eligibility/balance/reversal math
 * lives in `@helios/data-access` (patient-package-redemption-logic) and is
 * unit-tested there; this module does the DB I/O around it.
 *
 * A redemption: validates eligibility → guards over-redemption with a
 * conditional balance update → creates a zero-balance "covered by package"
 * invoice (never the original sale invoice) → writes the ledger row → flips
 * item/package status when fully used → deducts stock for tracked
 * product/medicine child items.
 *
 * Over-redemption is prevented transactionally at the data layer (a conditional
 * `UPDATE ... WHERE remaining_quantity >= qty`, verified by re-read), not only
 * in the UI. Cross-device concurrent redemption is additionally auditable via
 * the ledger. See the docstring gap note in the drawer/PR summary.
 */

import {
  applyRedemptionToBalance,
  applyReversalToBalance,
  allItemsFullyRedeemed,
  checkRedemptionEligibility,
  computeCoveredAmount,
} from '@helios/data-access';
import { deleteInvoiceCascade } from './delete-invoice-service';

interface PowerSyncLike {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

export interface RedeemInput {
  patientPackageId: string;
  patientPackageItemId: string;
  quantity: number;
  /** Encounter to attach to; its visit_id is stored on the redemption/invoice. */
  encounterId?: string | null;
  notes?: string | null;
  /** Optional idempotency key for offline retries. */
  clientReference?: string | null;
}

export interface RedeemResult {
  ok: boolean;
  error?: string;
  warning?: string;
  redemptionId?: string;
  invoiceId?: string;
  duplicate?: boolean;
}

interface ItemRow {
  id: string;
  org_id: string | null;
  patient_package_id: string | null;
  child_type: string | null;
  child_inventory_item_id: string | null;
  child_appointment_service_id: string | null;
  item_name_snapshot: string | null;
  item_code_snapshot: string | null;
  item_type_snapshot: string | null;
  unit_price_snapshot: number | null;
  redeemed_quantity: number | null;
  remaining_quantity: number | null;
  status: string | null;
}

interface PackageRow {
  id: string;
  org_id: string | null;
  patient_id: string | null;
  clinic_id: string | null;
  redemption_status: string | null;
  payment_status: string | null;
  valid_until: string | null;
  deleted_at: string | null;
  package_name_snapshot: string | null;
}

async function nextInvoiceNo(db: PowerSyncLike): Promise<string> {
  const rows = await db.getAll<{ invoice_no: string | null }>(`SELECT invoice_no FROM invoices`);
  const max = rows.reduce((m, r) => {
    const n = Number.parseInt((r.invoice_no ?? '').replace('INV-', ''), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return `INV-${String(max + 1).padStart(5, '0')}`;
}

/**
 * Redeem a package benefit (PRD.262 §4–§9). Returns a friendly `error` when
 * ineligible or when a concurrent redemption exhausted the balance.
 */
export async function redeemPatientPackageItem(params: {
  db: PowerSyncLike;
  input: RedeemInput;
  userId?: string | null;
}): Promise<RedeemResult> {
  const { db, input, userId = null } = params;
  const quantity = Number(input.quantity);

  const itemRows = await db.getAll<ItemRow>(
    `SELECT id, org_id, patient_package_id, child_type, child_inventory_item_id,
            child_appointment_service_id,
            item_name_snapshot, item_code_snapshot, item_type_snapshot, unit_price_snapshot,
            redeemed_quantity, remaining_quantity, status
       FROM patient_package_items WHERE id = ? AND deleted_at IS NULL`,
    [input.patientPackageItemId],
  );
  const item = itemRows[0];
  if (!item) return { ok: false, error: 'This benefit could not be found. Please refresh.' };

  const pkgRows = await db.getAll<PackageRow>(
    `SELECT id, org_id, patient_id, clinic_id, redemption_status, payment_status,
            valid_until, deleted_at, package_name_snapshot
       FROM patient_packages WHERE id = ?`,
    [input.patientPackageId],
  );
  const pkg = pkgRows[0];
  if (!pkg) return { ok: false, error: 'This package could not be found. Please refresh.' };

  const today = new Date().toISOString().slice(0, 10);
  const remaining = item.remaining_quantity ?? 0;
  const redeemed = item.redeemed_quantity ?? 0;

  const eligibility = checkRedemptionEligibility({
    deletedAt: pkg.deleted_at,
    redemptionStatus: pkg.redemption_status,
    paymentStatus: pkg.payment_status,
    validUntil: pkg.valid_until,
    itemStatus: item.status,
    remaining,
    quantity,
    today,
  });
  if (!eligibility.ok) return { ok: false, error: eligibility.error };

  const orgId = pkg.org_id ?? item.org_id;
  if (!orgId || !pkg.patient_id) {
    return { ok: false, error: 'Package ownership could not be created. Please try again.' };
  }

  // Idempotency: an earlier (possibly offline) submit already recorded this.
  if (input.clientReference) {
    const dup = await db.getAll<{ id: string; source_invoice_id: string | null }>(
      `SELECT id, source_invoice_id FROM patient_package_redemptions
        WHERE org_id = ? AND client_reference = ? AND deleted_at IS NULL LIMIT 1`,
      [orgId, input.clientReference],
    );
    if (dup[0]) {
      return {
        ok: true,
        redemptionId: dup[0].id,
        invoiceId: dup[0].source_invoice_id ?? undefined,
        duplicate: true,
      };
    }
  }

  // Resolve visit linkage from the encounter, if attached.
  let visitId: string | null = null;
  const encounterId = input.encounterId ?? null;
  if (encounterId) {
    const encRows = await db.getAll<{ visit_id: string | null }>(
      `SELECT visit_id FROM patient_encounters WHERE id = ?`,
      [encounterId],
    );
    visitId = encRows[0]?.visit_id ?? null;
  }

  const redemptionId = crypto.randomUUID();
  const invoiceId = crypto.randomUUID();
  const invoiceItemId = crypto.randomUUID();
  const now = new Date().toISOString();

  // ── Over-redemption guard: conditional balance update FIRST, then verify.
  const applied = applyRedemptionToBalance(redeemed, remaining, quantity);
  await db.execute(
    `UPDATE patient_package_items
        SET redeemed_quantity = ?, remaining_quantity = ?, status = ?, updated_at = ?
      WHERE id = ? AND deleted_at IS NULL AND remaining_quantity >= ?`,
    [applied.redeemedQuantity, applied.remainingQuantity, applied.status, now, item.id, quantity],
  );
  const afterRows = await db.getAll<{ redeemed_quantity: number | null }>(
    `SELECT redeemed_quantity FROM patient_package_items WHERE id = ?`,
    [item.id],
  );
  if ((afterRows[0]?.redeemed_quantity ?? redeemed) !== applied.redeemedQuantity) {
    // The conditional update did not apply — a concurrent redemption won.
    return {
      ok: false,
      error: 'This benefit no longer has enough remaining quantity. Please refresh and try again.',
    };
  }

  // PRD.267 — route on the child kind, never on item_type_snapshot (a 'service'
  // snapshot would fall back to medicine in the inventory behavior helpers).
  const isService = item.child_appointment_service_id != null;

  // Unit price snapshot → covered amount for the zero-balance invoice.
  let unitPrice = item.unit_price_snapshot;
  if (unitPrice === null || unitPrice === undefined) {
    if (isService) {
      const svcRows = await db.getAll<{ price_cents: number | null }>(
        `SELECT price_cents FROM appointment_services WHERE id = ?`,
        [item.child_appointment_service_id],
      );
      unitPrice = (svcRows[0]?.price_cents ?? 0) / 100;
    } else {
      const childRows = await db.getAll<{ selling_price: number | null }>(
        `SELECT selling_price FROM inventory_items WHERE id = ?`,
        [item.child_inventory_item_id],
      );
      unitPrice = childRows[0]?.selling_price ?? 0;
    }
  }
  const covered = computeCoveredAmount(unitPrice, quantity);

  // ── Zero-balance redemption invoice (mirrors the invoice create/save shape,
  // with a full "covered by package" discount so grand_total = 0).
  const patientRows = await db.getAll<{ full_name: string | null }>(
    `SELECT full_name FROM patients WHERE id = ?`,
    [pkg.patient_id],
  );
  const patientName = patientRows[0]?.full_name ?? 'Patient';
  const invoiceNo = await nextInvoiceNo(db);

  await db.execute(
    `INSERT INTO invoices
       (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
        invoice_no, patient_name, date_iso, status, notes,
        subtotal, discount_total, tax_total, grand_total, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'paid', 'Package redemption', ?, ?, 0, 0, ?, ?)`,
    [
      invoiceId,
      orgId,
      pkg.clinic_id ?? null,
      pkg.patient_id,
      encounterId,
      visitId,
      invoiceNo,
      patientName,
      today,
      covered,
      covered,
      now,
      now,
    ],
  );

  // PRD.267 — an appointment-service redemption line references the service
  // (service_id) and NEVER an inventory item, so it can't enter any stock queue
  // (Pharmacy / Product Release / Stock Card / Used Supplies are all gated on a
  // non-null inventory_item_id). Inventory children keep the existing behavior.
  await db.execute(
    `INSERT INTO invoice_items
       (id, org_id, invoice_id, service_name, service_id, qty, unit_price, discount, discount_type, tax_rate,
        inventory_item_id, free_text_name,
        description_snapshot, sku_snapshot, unit_snapshot, unit_price_snapshot,
        tax_inclusive_snapshot, open_pricing_snapshot, source_type, source_id,
        remarks, add_to_treatment, sort_order, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'fixed', 0,
             ?, NULL,
             ?, ?, NULL, ?,
             NULL, NULL, 'package_redemption', ?,
             ?, 0, 0, ?, ?)`,
    [
      invoiceItemId,
      orgId,
      invoiceId,
      item.item_name_snapshot ?? 'Item',
      isService ? item.child_appointment_service_id : null,
      quantity,
      unitPrice,
      covered, // full discount → line total 0
      isService ? null : item.child_inventory_item_id,
      item.item_name_snapshot ?? 'Item',
      item.item_code_snapshot ?? null,
      unitPrice,
      redemptionId,
      `Covered by package: ${pkg.package_name_snapshot ?? 'Package'}`,
      now,
      now,
    ],
  );

  // ── Ledger row (PRD.267 — preserves the child kind + references).
  await db.execute(
    `INSERT INTO patient_package_redemptions
       (id, org_id, patient_id, clinic_id, patient_package_id, patient_package_item_id,
        child_type, child_inventory_item_id, child_appointment_service_id,
        source_invoice_id, source_invoice_item_id, visit_id,
        redeemed_quantity, item_name_snapshot, item_code_snapshot, item_type_snapshot,
        unit_price_snapshot, covered_amount, redeemed_at, redeemed_by, notes, status,
        client_reference, created_at, updated_at, deleted_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'posted', ?, ?, ?, NULL)`,
    [
      redemptionId,
      orgId,
      pkg.patient_id,
      pkg.clinic_id ?? null,
      pkg.id,
      item.id,
      isService ? 'appointment_service' : 'inventory_item',
      isService ? null : item.child_inventory_item_id,
      isService ? item.child_appointment_service_id : null,
      invoiceId,
      invoiceItemId,
      visitId,
      quantity,
      item.item_name_snapshot ?? 'Item',
      item.item_code_snapshot ?? null,
      item.item_type_snapshot ?? 'product',
      unitPrice,
      covered,
      now,
      userId,
      input.notes?.trim() || null,
      input.clientReference ?? null,
      now,
      now,
    ],
  );

  // ── Flip the package to fully_redeemed when every benefit is used up.
  const remainingRows = await db.getAll<{ remaining_quantity: number | null }>(
    `SELECT remaining_quantity FROM patient_package_items
      WHERE patient_package_id = ? AND deleted_at IS NULL`,
    [pkg.id],
  );
  if (
    allItemsFullyRedeemed(
      remainingRows.map((r) => ({ remainingQuantity: r.remaining_quantity ?? 0 })),
    )
  ) {
    await db.execute(
      `UPDATE patient_packages SET redemption_status = 'fully_redeemed', updated_at = ?
        WHERE id = ? AND redemption_status = 'active'`,
      [now, pkg.id],
    );
  }

  // ── Fulfillment (PRD.262 §4, §9). We do NOT deduct stock here and never ask
  // the redeeming user to pick a batch. Instead the paid zero-balance
  // redemption invoice line IS the pending fulfillment task:
  //   - medicine / dispense_via_pharmacy product child → auto-appears in the
  //     Pharmacy queue ("Ready for Pharmacy"), where Pharmacy picks the batch
  //     and deducts stock via the existing Confirm Dispense flow.
  //   - regular product child → auto-appears as "Ready for Release" (Product
  //     Release), which deducts stock on release.
  //   - service child → enters no queue, so it is inherently `not_required`.
  // Both queues derive purely from a paid invoice line pointing to the child
  // inventory item, so no explicit pending row (and no stock movement) is
  // written from this drawer.

  return { ok: true, redemptionId, invoiceId, warning: eligibility.warning };
}

export interface ReverseResult {
  ok: boolean;
  error?: string;
}

/**
 * Fully reverse a posted redemption (PRD.262 §11, §12). Admin/billing-manager
 * gated in the UI. Restores the benefit balance, marks the ledger row reversed,
 * and cancels the pending fulfillment task by cascade-deleting the redemption
 * invoice (which removes it from the Pharmacy / Product Release queue).
 *
 * If Pharmacy already dispensed / released the redeemed item, reversal is
 * BLOCKED with a clear message — automated stock return + dispense reversal is
 * intentionally deferred (see gap note). Staff must reverse the dispense in
 * Pharmacy first.
 */
export async function reversePatientPackageRedemption(params: {
  db: PowerSyncLike;
  redemptionId: string;
  reason: string;
  userId?: string | null;
}): Promise<ReverseResult> {
  const { db, redemptionId, reason, userId = null } = params;
  if (!reason.trim()) return { ok: false, error: 'A reason is required to reverse a redemption.' };

  const rows = await db.getAll<{
    id: string;
    patient_package_id: string | null;
    patient_package_item_id: string | null;
    child_inventory_item_id: string | null;
    source_invoice_id: string | null;
    source_invoice_item_id: string | null;
    redeemed_quantity: number | null;
    item_type_snapshot: string | null;
    status: string | null;
    deleted_at: string | null;
  }>(
    `SELECT id, patient_package_id, patient_package_item_id, child_inventory_item_id,
            source_invoice_id, source_invoice_item_id, redeemed_quantity, item_type_snapshot,
            status, deleted_at
       FROM patient_package_redemptions WHERE id = ?`,
    [redemptionId],
  );
  const r = rows[0];
  if (!r || r.deleted_at) return { ok: false, error: 'Redemption not found.' };
  if (r.status !== 'posted')
    return { ok: false, error: 'This redemption has already been reversed.' };

  const pkgRows = await db.getAll<{ redemption_status: string | null; deleted_at: string | null }>(
    `SELECT redemption_status, deleted_at FROM patient_packages WHERE id = ?`,
    [r.patient_package_id],
  );
  const pkg = pkgRows[0];
  if (pkg?.deleted_at || pkg?.redemption_status === 'voided') {
    return { ok: false, error: 'Cannot reverse: this package has been voided.' };
  }

  // Block reversal if Pharmacy already dispensed / released this benefit —
  // deleting the invoice would orphan the dispense/release rows and leave stock
  // deducted. Staff must reverse the dispense in Pharmacy first (gap: automated
  // stock return on reversal is deferred to a Pharmacy-correction flow).
  if (r.source_invoice_item_id) {
    const fulfilled = await db.getAll<{ done: number | null }>(
      `SELECT
         (SELECT COALESCE(SUM(quantity_dispensed), 0) FROM pharmacy_dispense_lines
           WHERE invoice_item_id = ? AND status = 'confirmed')
       + (SELECT COALESCE(SUM(quantity_released), 0) FROM product_release_lines
           WHERE invoice_item_id = ? AND status = 'confirmed') AS done`,
      [r.source_invoice_item_id, r.source_invoice_item_id],
    );
    if ((fulfilled[0]?.done ?? 0) > 0) {
      return {
        ok: false,
        error:
          'This benefit has already been dispensed by Pharmacy. Reverse the dispense in Pharmacy before reversing the redemption.',
      };
    }
  }

  const now = new Date().toISOString();
  const qty = r.redeemed_quantity ?? 0;

  // Restore item balance.
  const itemRows = await db.getAll<{
    redeemed_quantity: number | null;
    remaining_quantity: number | null;
  }>(`SELECT redeemed_quantity, remaining_quantity FROM patient_package_items WHERE id = ?`, [
    r.patient_package_item_id,
  ]);
  const it = itemRows[0];
  if (it) {
    const restored = applyReversalToBalance(
      it.redeemed_quantity ?? 0,
      it.remaining_quantity ?? 0,
      qty,
    );
    await db.execute(
      `UPDATE patient_package_items
          SET redeemed_quantity = ?, remaining_quantity = ?, status = ?, updated_at = ?
        WHERE id = ?`,
      [
        restored.redeemedQuantity,
        restored.remainingQuantity,
        restored.status,
        now,
        r.patient_package_item_id,
      ],
    );
  }

  // Re-open a fully_redeemed package now that a benefit returned.
  if (pkg?.redemption_status === 'fully_redeemed') {
    await db.execute(
      `UPDATE patient_packages SET redemption_status = 'active', updated_at = ? WHERE id = ?`,
      [now, r.patient_package_id],
    );
  }

  // Mark the ledger row reversed.
  await db.execute(
    `UPDATE patient_package_redemptions
        SET status = 'reversed', reversed_at = ?, reversed_by = ?, reverse_reason = ?, updated_at = ?
      WHERE id = ?`,
    [now, userId, reason.trim(), now, redemptionId],
  );

  // Cancel the pending fulfillment task + void the zero-balance redemption
  // invoice via the existing cascade delete. Because the item was not yet
  // dispensed/released (guarded above), no stock was moved — nothing to return.
  if (r.source_invoice_id) {
    await deleteInvoiceCascade({ db, invoiceId: r.source_invoice_id });
  }

  return { ok: true };
}
